import { create } from 'zustand';
import * as api from '../api/conciliatron';
import { ApiError, configure } from '../api/client';
import { ENV } from '../config/env';
import {
  buildBatchRequest,
  batchRequestToDraft,
  type AlgorithmInput,
  type ReaderInput,
  type ReconciliationDraft,
} from '../lib/batchRequestBuilder';
import { inheritedParameters } from '../config/algorithms';
import { fileDisplayName, readerFieldsFromMetadata } from '../lib/fileMetadata';
import { fetchFileMetadata } from '../api/storagehub';
import { chartCategoryMap, chartInstanceMap, resultToDashboard } from '../lib/resultToDashboard';
import { executedInstanceIds, mintFigureTitle, mintInstanceId, takenInstanceIds } from '../lib/algorithmInstance';
import { ALGORITHM_OVERLAY } from '../config/catalogOverlay';
import { isTonnageAlgorithm, tonnageFigureLabel } from '../config/measures';
import { loadMe } from '../lib/me';
import { readRememberedWorkspace, rememberWorkspace } from '../lib/rememberedWorkspace';
import {
  applyChartLimits,
  applyChartRedraw,
  applyChartSetChange,
  withoutDerivedSubtitles,
  hasCharts,
  reattachChartData,
  withDerivedReferenceLines,
  stripChartData,
} from '../lib/snapshotData';
import {
  hasBeenExecuted,
  hasResultStatus,
  isPending,
  isPublished,
  isTerminalStatus,
  ReconciliationStatus,
  SEALED_ANALYSIS_MESSAGE,
  validateName,
  validateViewName,
  copyName,
  type ChartNote,
  type ExecutionError,
  type Recipient,
  type Reconciliation,
  type ReconciliationView,
  type UpdateViewBody,
} from '../types/api';
import type { ResultJson } from '../types/results';
import type { BatchRequest } from '../types/compute';
import type { DashboardConfig } from '@micronet/dashboard-kit';
import {
  defaultBlocks,
  migrateNoteBlocks,
  reportFigures,
  newBlock,
  insertBlock,
  reorderBlocks,
  reportTitle,
  type BlockPatch,
  type ReportBlock,
  type ReportBlockKind,
} from '../lib/reportBlocks';
import { executedConfigOf, presentedConfigOf } from '../lib/executedConfig';
import { isPartialRunPending, mergePartialDashboard } from '../lib/partialRun';

/**
 * The figures a report block can point at, tagged with everything that names them.
 *
 * Both places that build this list must pass the SAME three arguments: drop the
 * category map and every block is seeded referencing a position instead, so
 * unpicking one category of a split silently repoints a caption onto a neighbour's
 * curve. That is the whole failure this reference model exists to prevent, and it
 * came back through a caller that simply omitted an optional parameter.
 */
function figuresOf(
  charts: DashboardConfig['charts'],
  result: ResultJson | null,
  current: Reconciliation | null
) {
  if (!result) return reportFigures(charts, {});
  const executed = executedConfigOf(current);
  return reportFigures(
    charts,
    chartInstanceMap(result, executed),
    chartCategoryMap(result, executed)
  );
}

// Defaults that make a freshly-added CSV immediately runnable — they
// mirror the wrapper happy-path fixture (e2e_test.go).
const READER_DEFAULTS = {
  delimiter: ',',
  hasHeader: true,
  coordinatesIndexes: [0, 1, 2] as [number, number, number],
  delta: [10, 10, 10] as [number, number, number],
  min: [0, 0, 0] as [number, number, number],
  max: [20, 20, 10] as [number, number, number],
};

// Async execution is dispatch + poll: POST /execute returns 202 before the
// worker runs, so we poll GET until the reconciliation reaches a terminal
// status. The run is a durable background job (SQS → worker → DB), so there is
// no client-side deadline — a slow / cold-starting worker must not surface a
// false timeout; we observe until it settles or the poll is abandoned. Tunables,
// not magic numbers.
const POLL_INTERVAL_MS = 1500;
// On consecutive transient poll failures (timeout / network / 5xx) back off so a
// stalled backend isn't hammered, capped so recovery stays timely once it heals.
const POLL_BACKOFF_MAX_MS = 15_000;
const pollBackoffMs = (failures: number): number =>
  Math.min(POLL_INTERVAL_MS * 2 ** (failures - 1), POLL_BACKOFF_MAX_MS);

const emptyDraft = (): ReconciliationDraft => ({ readers: [], algorithms: [] });

/**
 * The saved figure names, written into the charts that draw them (SPEC/2240) —
 * the commit point of a rename, which updateAlgorithm no longer applies per
 * keystroke. Only for an instance drawing exactly ONE figure, the same scope the
 * per-keystroke sync had: one with several (the `error` algorithm's LP/CP/SP
 * bases, the tonnage-by-category fan-out) keeps its titles until the next
 * execution, where resultToDashboard numbers them (`name`, `name #2`, …).
 * Returns `shown` itself when nothing changes.
 */
function withFigureTitles(
  shown: DashboardConfig,
  owners: Record<string, string>,
  config: BatchRequest
): DashboardConfig {
  const titleOf = new Map(
    config.algorithms
      .filter((a) => a.instance_id && a.figure_title)
      .map((a) => [a.instance_id!, a.figure_title!])
  );
  const drawn = new Map<string, number>();
  for (const c of shown.charts) {
    const id = owners[c.id];
    if (id) drawn.set(id, (drawn.get(id) ?? 0) + 1);
  }
  let changed = false;
  const charts = shown.charts.map((c) => {
    const id = owners[c.id];
    const title = id && drawn.get(id) === 1 ? titleOf.get(id) : undefined;
    if (!title || title === c.title) return c;
    changed = true;
    return { ...c, title };
  });
  return changed ? { ...shown, charts } : shown;
}

/** Every figureTitle already claimed in the draft (SPEC/2240) — what
 *  mintFigureTitle must avoid when minting a name for a new or duplicated
 *  instance. */
const figureTitlesInUse = (draft: ReconciliationDraft): string[] =>
  draft.algorithms.map((a) => a.figureTitle).filter((t): t is string => !!t);

function errMessage(e: unknown): string {
  if (e instanceof ApiError) return `${e.code}: ${e.message}`;
  return e instanceof Error ? e.message : String(e);
}

/**
 * The one backend refusal a person can act on, said in the interface's voice.
 *
 * Everything else falls through to errMessage, which prefixes the backend's
 * English with its code — acceptable for a state conflict the user cannot fix,
 * wrong for a name they are in the middle of typing. Matched on the CODE, not the
 * status: 409 also means "not published", which is a different instruction.
 */
const DUPLICATE_VIEW_NAME_MESSAGE = 'Ya existe una vista con ese nombre.';
function viewNameErrMessage(e: unknown): string {
  if (e instanceof ApiError && e.code === 'duplicate_name') return DUPLICATE_VIEW_NAME_MESSAGE;
  return errMessage(e);
}

// A failed run stores `{ error }` in executionResult (statusId === Error).
// Surface a readable message in the interface's voice.
function executionErrorMessage(rec: Reconciliation): string {
  const payload = rec.executionResult;
  if (payload && !Array.isArray(payload) && 'error' in payload) {
    const err = (payload as ExecutionError).error;
    if (typeof err === 'string') return err;
    if (err != null) return JSON.stringify(err);
  }
  return 'La ejecución falló.';
}

// A failed partial run's error (Bug 2609), in the same voice as a failed run.
function lastRunErrorMessage(rec: Reconciliation): string | null {
  return rec.lastRunError ? executionErrorMessage({ ...rec, executionResult: rec.lastRunError }) : null;
}

// A session presentation override, tagged with the result version (executedAt)
// it was built against. A re-execution changes executedAt, so a stale override
// from a previous run is ignored and the dashboard is rebuilt from the new data.
interface DashboardOverride {
  version: string | null;
  config: DashboardConfig;
}

// Build the plotting surface from a reconciliation, but only when it holds a
// successful result (a bare envelope array). A session override wins ONLY when
// it matches the current result version, so editing presentation then changing
// config/files/algorithms and re-executing never shows the previous dashboard.
function deriveResults(
  rec: Reconciliation,
  overrides: Record<number, DashboardOverride>
): { result: ResultJson | null; dashboard: DashboardConfig | null } {
  // Executed OR Published both carry a successful result array.
  if (!hasResultStatus(rec.statusId) || !Array.isArray(rec.executionResult)) {
    return { result: null, dashboard: null };
  }
  const result: ResultJson = { results: rec.executionResult };
  const ov = overrides[rec.id];
  const fresh = ov && ov.version === (rec.executedAt ?? null);
  // Pass the config the run was EXECUTED with so charts can read their algorithm
  // inputs (the entered cut-off grades for the tonnage x axis, the scatter's
  // cross-hair). Correlation is by name + occurrence, so it has to be the executed
  // config: saving without re-executing leaves the saved one a different length and
  // every envelope after the gap reads a neighbour's parameters (lib/executedConfig).
  // The one exception is the saved `plot_measures`, presentation-only and lifted
  // by instance id (presentedConfigOf, SPEC/2515).
  return {
    result,
    dashboard: fresh ? ov.config : resultToDashboard(result, presentedConfigOf(rec)),
  };
}

// Bug 2794: only the charts of analyses the SAVED config still has. The stored
// result keeps a deleted analysis' envelope until the next run, so every way of
// rebuilding the dashboard on open — the derivation, a stored shape that adds what
// it never named, a session override — drew it again after Guardar. The ownership
// rule save() applies before adding a chart, except that charts are attributed
// through the EXECUTED config (the one they were drawn from), not the saved one,
// which no longer pairs with the stored envelopes. Null when nothing is left, so the
// panel shows the placeholder, as deleting the last chart in session does. A
// published reconciliation is sealed (config = what ran), so it is left alone.
function withoutDeletedCharts(
  dashboard: DashboardConfig | null,
  result: ResultJson | null,
  rec: Reconciliation
): DashboardConfig | null {
  if (!dashboard || !result || isPublished(rec)) return dashboard;
  const owners = chartInstanceMap(result, presentedConfigOf(rec));
  const owned = new Set(executedInstanceIds(rec.config));
  // A chart no instance produced is not attributable, so not a deleted one.
  const charts = dashboard.charts.filter((c) => !(c.id in owners) || owned.has(owners[c.id]));
  if (charts.length === dashboard.charts.length) return dashboard;
  return charts.length > 0 ? { ...dashboard, charts } : null;
}

// Replace a reconciliation in the list (or prepend if new) — keeps the sidebar
// fresh after save/execute without a full list refetch.
/**
 * Replace a reconciliation in the list, or prepend if new — KEEPING the row's
 * recorded history when the incoming one says nothing about it.
 *
 * Only `GET /reconciliations` carries `tracks`; every single-row response (save,
 * publish, the execute poll, clone, the re-read after sending a view) omits the
 * field, and all of them upsert locally instead of refetching the whole list.
 * Assigning the response as-is therefore dropped the row's history on the very
 * actions that produce history — silently: the row stayed, with the right name
 * and status, and only its timeline went blank until a full refetch.
 *
 * The distinction is only expressible because the backend omits the field
 * rather than sending `[]`: absent = "not part of this response", empty = "no
 * actions". A response that DOES carry tracks wins, or this would be a cache
 * that never invalidates.
 */
function upsertList(list: Reconciliation[], rec: Reconciliation): Reconciliation[] {
  const i = list.findIndex((r) => r.id === rec.id);
  if (i === -1) return [rec, ...list];
  const next = list.slice();
  next[i] = rec.tracks === undefined ? { ...rec, tracks: list[i].tracks } : rec;
  return next;
}

// Replace a view in the list, or append if new. APPEND, not prepend: the
// backend orders views oldest-first (created_at is their order), so a new one
// belongs at the end. The list it replaces was newest-first and the client took
// items[0] as "the published one" — which is exactly how US 2100 showed "no se
// encontró el documento publicado" over a report that existed.
function upsertView(list: ReconciliationView[], v: ReconciliationView): ReconciliationView[] {
  const i = list.findIndex((x) => x.id === v.id);
  if (i === -1) return [...list, v];
  const next = list.slice();
  next[i] = v;
  return next;
}

// The DashboardConfig the document should be rendered from: the user's session
// override when it still matches the current result version, else the freshly
// derived dashboard. Mirrors deriveResults' override-freshness rule so the
// document captures exactly what the user sees.
function resolveDashboard(s: {
  currentId: number | null;
  current: Reconciliation | null;
  dashboard: DashboardConfig | null;
  dashboardOverrides: Record<number, DashboardOverride>;
}): DashboardConfig | null {
  if (s.currentId == null) return s.dashboard;
  const ov = s.dashboardOverrides[s.currentId];
  const fresh = ov && ov.version === (s.current?.executedAt ?? null);
  return fresh ? ov.config : s.dashboard;
}

// Cancellation token for the execution poller — bump to abandon a stale loop.
let pollToken = 0;

// Resolves the reconciliation-delete ask in flight (Bug 2797) — a function, so it
// lives beside the store rather than in it; `deleteConfirmReconciliation` is the
// state the dialog renders from.
let pendingDeleteAnswer: ((confirmed: boolean) => void) | null = null;

// Shown when an action runs before init() has resolved a workspace from /me.
const NO_WORKSPACE = 'No hay un workspace activo.';

// Narrows the store's nullable workspaceId for the actions below.
//
// workspaceId is null only between the store's creation and init()'s answer from
// /me — it is no longer seeded to a build-time constant (config/env.ts), which is
// what used to make the first request of every session name workspace 1. Every
// action that calls this needs a listed or open reconciliation, and only a
// resolved workspace produces one, so a null means that gate was bypassed. Throw
// into the caller's existing catch rather than requesting /workspaces/null/... —
// the 404 that comes back reads as "this reconciliation is gone", which is a lie.
function requireWorkspaceId(workspaceId: number | null): number {
  if (workspaceId === null) throw new Error(NO_WORKSPACE);
  return workspaceId;
}

// Poll the reconciliation until it settles (Executed/Error), tolerating
// transient failures so a slow or cold-starting worker never aborts the run.
// Reused by execute() (after dispatch) and openReconciliation() (to resume a
// run still in flight). The caller owns the initial `running: true`/tab; this
// clears `running` only when the run settles or hits a non-recoverable error.
// Cancellation rides the shared pollToken: a navigation/delete bumps it and the
// next tick bails, so a stale loop can't write into a superseded view. Defined
// at module scope (not inside create) so it reaches store state via useStore's
// own getState/setState without reindenting the whole store; it only runs at
// call time, long after useStore is initialised.
async function pollUntilSettled(id: number, token: number): Promise<void> {
  let failures = 0;
  const tick = async (): Promise<void> => {
    if (token !== pollToken) return;
    // Guarded rather than narrowed: a throw here would land in the catch below and
    // be retried with a backoff forever, since "no workspace" is not transient.
    const workspaceId = useStore.getState().workspaceId;
    if (workspaceId === null) return;
    let rec: Reconciliation;
    try {
      rec = await api.getReconciliation(workspaceId, id);
    } catch (e) {
      if (token !== pollToken) return;
      // A 404 means the reconciliation is gone — stop and surface it. Every
      // other failure (timeout / network / 5xx) is transient: keep polling with
      // a backoff so a stalled or cold worker doesn't kill a live run.
      if (e instanceof ApiError && e.status === 404) {
        useStore.setState({ error: errMessage(e), running: false });
        return;
      }
      failures += 1;
      setTimeout(() => void tick(), pollBackoffMs(failures));
      return;
    }
    if (token !== pollToken) return;
    failures = 0;
    // What the shown dashboard was derived from, read before `current` is
    // overwritten: a partial run's merge needs it to tell a derived subtitle from
    // one the user typed (Bug 2753). Until the run lands, the executed pair is
    // still the pre-run one.
    const prior = useStore.getState();
    // Keep the sidebar row's status in sync without a list refetch.
    useStore.setState((s) => ({
      current: rec,
      reconciliations: upsertList(s.reconciliations, rec),
    }));
    // A partial run leaves the status alone (Bug 2609), so a terminal status is
    // not "settled" while the backend still holds its plan.
    if (isTerminalStatus(rec.statusId) && !isPartialRunPending(rec)) {
      const { runningInstanceIds, dashboard: shown, dashboardOverrides } = useStore.getState();
      if (rec.statusId === ReconciliationStatus.Executed) {
        const { result, dashboard } = deriveResults(rec, dashboardOverrides);
        // A partial run changed a few charts of a result the user has been
        // arranging: keep their arrangement (mergePartialDashboard). A full
        // run's figures are all new, and are drawn fresh as before.
        const merged =
          runningInstanceIds && result && dashboard
            ? mergePartialDashboard(
                shown,
                dashboard,
                chartInstanceMap(result, executedConfigOf(rec)),
                runningInstanceIds,
                prior.result && resultToDashboard(prior.result, presentedConfigOf(prior.current))
              )
            : dashboard;
        // Clear any prior error so success ⇒ no error is an explicit invariant.
        // A failed partial run is the exception that is not an `error`: the
        // result it left untouched is still a success (partialRunError).
        useStore.setState({
          result,
          dashboard: merged,
          running: false,
          runningInstanceIds: null,
          error: null,
          partialRunError: lastRunErrorMessage(rec),
        });
      } else {
        useStore.setState({
          error: executionErrorMessage(rec),
          running: false,
          runningInstanceIds: null,
        });
      }
      return;
    }
    setTimeout(() => void tick(), POLL_INTERVAL_MS);
  };
  await tick();
}

/**
 * The reconciliation workflow as main-area tabs, in order: config → results →
 * params (the read-only config that produced the results) → report. A single
 * string-union type (not two booleans) is the one source of truth for which
 * surface is shown; the shared tab strip and the initial tab on open both
 * derive from it.
 */
// 'results' is the surface INSIDE Gráficos (with the sidebar, open state);
// 'dashboard' is the published surface (no sidebar, read-only configuration).
// They are not interchangeable — see WorkflowTabs.
export type MainTab = 'config' | 'results' | 'dashboard' | 'report';

/**
 * Top-level app sections (App.tsx tabs). "Workspace" is the open
 * reconciliation; "Reconciliaciones" is the list. Opening a reconciliation (or
 * starting a new one) switches the section to the workspace.
 */
export type TopSection =
  | 'reconciliaciones'
  | 'archivos'
  | 'espacio-trabajo'
  | 'equipo'
  | 'herramientas';

export interface PickedFile {
  id: string;
  filename: string;
  /** storage-hub file_metadata blob (columns + grid), when available. */
  metadata?: Record<string, unknown>;
}

interface State {
  /** The workspace every request below is scoped to, or null before init()
   *  resolves one from /me. Never seeded to a constant — see config/env.ts. */
  workspaceId: number | null;
  /** Whether init() already restored the remembered workspace in this page
   *  load. A later init() (App re-runs it on an MSAL account event) keeps the
   *  workspace the tab is in instead of adopting another tab's last pick. */
  workspaceRestored: boolean;
  reconciliations: Reconciliation[];
  loadingList: boolean;

  currentId: number | null;
  name: string;
  description: string;
  draft: ReconciliationDraft;
  /** UI-only cache: storage-hub file id → display name, for spatial-filter solids.
   *  Keeps `SpatialFilter.solids` (the wire contract) pristine while the editor
   *  shows names instead of raw ids. Populated on pick and rehydrated on reopen
   *  (mirrors readers' `fileName`). Keyed by globally-unique file id, so entries
   *  stay valid across reconciliations. */
  solidNames: Record<string, string>;
  dirty: boolean;
  saving: boolean;

  /** The open reconciliation's server snapshot (status, executedAt, result). */
  current: Reconciliation | null;
  /** A GET for the reconciliation being opened is in flight (openReconciliation). */
  loadingCurrent: boolean;
  /** A reader-metadata fetch is in flight. While true the column pickers show a
   *  loading state instead of falling back to manual entry — the fallback looks
   *  like a normal (empty) form and lets a column be bound past the type filter. */
  hydratingColumns: boolean;
  /** File ids whose metadata fetch failed. Without this, a failed fetch is
   *  indistinguishable from a file that genuinely carries no metadata, and the
   *  editor silently drops to manual entry for the rest of the session. */
  columnsFetchFailed: string[];
  running: boolean;
  /** The charts a partial run in flight computes (Bug 2609); null for a full
   *  run or none. While set, the other charts stay on screen. */
  runningInstanceIds: string[] | null;
  /** A failed partial run's message. Not `error`: that one replaces the
   *  dashboard, and a partial failure left every other chart's figures intact. */
  partialRunError: string | null;
  result: ResultJson | null;
  dashboard: DashboardConfig | null;
  /** Per-reconciliation presentation edits to the result dashboard, kept for the
   *  session so re-opening restores the user's tweaks. Not persisted to the
   *  backend (a page reload starts from the freshly-built default). */
  dashboardOverrides: Record<number, DashboardOverride>;

  /** A views GET (loadViews) is in flight. True from the moment
   *  openReconciliation kicks it off for a dashboard-bearing reconciliation
   *  until it settles. For a published reconciliation, `dashboard` is set
   *  synchronously on open from `workingDashboardConfig` — the publish-time
   *  snapshot, never updated afterward — before the actually-selected view's
   *  presentation (where an edit lives) has been read back. Consumers that
   *  render the published dashboard gate on this so they never paint that
   *  stale snapshot while the real one is still in flight (#2449). */
  loadingViews: boolean;

  /** The open reconciliation's views, oldest first. Empty while it is open —
   *  views only exist once it is published. */
  views: ReconciliationView[];
  /** Which view the dashboard and the report are showing, or null. */
  selectedViewId: number | null;
  /** A view write (shape, notes, report, title) is in flight. */
  savingView: boolean;
  /** A view is being created. Separate from savingView so the save button
   *  does not announce "Guardando…" while a DIFFERENT action is in flight. */
  creatingView: boolean;

  /** A "Publicar" or "Marcar como enviada" call is in flight. */
  publishing: boolean;

  /** The report being composed: an ordered list of blocks the user adds /
   *  reorders / removes (lib/reportBlocks) — including the document's own `title`
   *  block, which is where the title lives. There is no separate `docTitle`: one
   *  list, one source of truth, and `reportTitle(blocks)` reads it back.
   *
   *  The blocks belong to the SELECTED VIEW and are rehydrated from it, so a
   *  report now survives a reload. They used to be session-only — cleared on
   *  every open — and the PDF was the only thing that outlived the tab. */
  blocks: ReportBlock[];
  /** Notes around each figure of the selected view, keyed by chart id. */
  chartNotes: Record<string, ChartNote>;

  /**
   * Which main-area surface is shown: the config editor, the execution results
   * (charts / running / error), or the report-template document editor
   * (SPEC/1466 phase 3, entered via "Ir al reporte"). On open it's derived
   * from the reconciliation's status (an executed one lands on results).
   */
  activeTab: MainTab;

  /** Top-level app section (App.tsx tabs); the list vs the open workspace. */
  section: TopSection;

  /** Instance id whose config modal (parameters + presentation) is open, or null.
   *  Store-owned so both the batch list and a chart card open the same editor. */
  configModalInstance: string | null;
  /** Which figure of that instance the Presentation tab edits; null → its first. */
  configModalChartId: string | null;

  /**
   * Instance id awaiting a delete confirmation, or null. Store-owned for the same
   * reason as `configModalInstance`: the gesture is offered by three surfaces (the
   * sidebar tree, the pending cards, the dashboard cards) and they must not each
   * grow their own dialog — see useChartInstanceActions, which routes all three
   * through one implementation.
   */
  deleteConfirmInstance: string | null;

  /**
   * Reconciliation id awaiting a delete confirmation, or null (Bug 2797). Set by
   * `deleteReconciliation` itself, not by the surfaces: five of them offer the
   * gesture, and the ask belonging to the action is what keeps any of them —
   * or the next one — from deleting on a single click.
   */
  deleteConfirmReconciliation: number | null;

  /**
   * How many ConfirmDeleteReconciliation dialogs are mounted. With none,
   * `deleteReconciliation` has nobody to ask and deletes directly (pure store
   * use); App mounts one, so in the running app there always is.
   */
  reconciliationDeleteConfirmers: number;

  /**
   * The chart instance selected in the unified workspace, as
   * the minted `AlgorithmInput.instanceId` (see lib/algorithmInstance +
   * resultToDashboard's chartInstanceMap), or null when nothing is selected.
   * UI-only; reset on open / startNew. Lets the workspace show a chart's config
   * in context without leaving the dashboard.
   */
  selectedInstanceId: string | null;

  error: string | null;

  /** Resolve the caller's starting workspace from core, then load its list.
   *  Takes the token getter because the workspace can only come from `/me`. */
  init: (getAccessToken: (forceRefresh?: boolean) => Promise<string>) => Promise<void>;
  fetchReconciliations: () => Promise<void>;
  startNew: () => void;
  /** Switches the app's active workspace (e.g. from the Equipo/Workspace tab's
   *  switcher): updates the X-Authz-Workspace-Ids header used by every conciliatron/
   *  storage-hub call, discards the open reconciliation (it belonged to the old
   *  workspace), and re-fetches the reconciliations list for the new one. Does not
   *  change `section` — stays wherever the switch happened from. */
  setWorkspaceId: (workspaceId: number) => void;
  openReconciliation: (id: number) => Promise<void>;
  cloneReconciliation: (id: number) => Promise<void>;
  setName: (v: string) => void;
  setDescription: (v: string) => void;
  /** Put a draft snapshot back, with the dirty flag it had when taken. The
   *  config forms edit the live draft as the user types, so a modal's "Cancelar"
   *  has nothing to discard unless it can restore what was there on open. */
  restoreDraft: (snapshot: { draft: ReconciliationDraft; dirty: boolean }) => void;
  addReaderForFile: (file: PickedFile) => void;
  updateReader: (index: number, patch: Partial<ReaderInput>) => void;
  removeReader: (index: number) => void;
  hydrateReaderColumns: () => Promise<void>;
  /** Merge id→display-name entries into the solid-name cache (used on pick). */
  noteSolidNames: (entries: Record<string, string>) => void;
  /** Fetch display names for every solid referenced in the draft that isn't cached
   *  yet, so a reopened reconciliation shows names (mirrors hydrateReaderColumns). */
  hydrateSolidNames: () => Promise<void>;
  addAlgorithm: (name: string) => void;
  updateAlgorithm: (index: number, a: AlgorithmInput) => void;
  removeAlgorithm: (index: number) => void;
  duplicateAlgorithm: (index: number) => void;
  save: () => Promise<number | null>;
  /** Persist edits, dispatch the worker, and poll until the result lands.
   *  With `instanceIds`, only those charts run and merge into the stored result
   *  (Bug 2609) — the chart modal's Ejecutar; without, the whole batch. */
  execute: (instanceIds?: string[]) => Promise<void>;
  /** Deletes a reconciliation — after asking, when a confirmation is mounted
   *  (Bug 2797). A "no" resolves without touching the API. */
  deleteReconciliation: (id: number) => Promise<void>;
  /** Answer the pending delete ask; false for Cancelar / Escape / close. */
  answerReconciliationDelete: (confirmed: boolean) => void;
  /** Register a confirmation dialog; returns its unregister, which cancels a
   *  pending ask when the last one goes. */
  mountReconciliationDeleteConfirm: () => () => void;
  /** Record the user's presentation edits to a reconciliation's dashboard.
   *  `userEdit` says this came from a gesture the user made, and is what
   *  re-enables Guardar — see the implementation for why it is not implied. */
  setDashboardOverride: (
    reconciliationId: number,
    config: DashboardConfig,
    opts?: { userEdit?: boolean }
  ) => void;
  /** "Publicar": seal the reconciliation and open its view #1 (D1).
   *  Irreversible. Resolves to an error message on failure (surfaced inline by
   *  the caller) or null on success.
   *
   *  `viewName` names view #1. Optional, unlike newView's: publishing is the
   *  irreversible action and must not be blocked by a text field, and it is the
   *  one create with no second caller to collide with — `published_at IS NULL`
   *  picks a single winner — so the backend can safely fall back to "Vista 1". */
  publish: (viewName?: string) => Promise<string | null>;
  /** Load the open reconciliation's views and select one (read-back). */
  loadViews: (reconciliationId: number) => Promise<void>;
  /** Read-only fetch of a reconciliation's views for the history detail panel
   *  — does NOT mutate the workspace (dashboard/current). Rejects on failure
   *  (logged) so the caller can distinguish "load error" from "no views"; it
   *  never touches the shared execution `error` banner. */
  fetchViews: (reconciliationId: number) => Promise<ReconciliationView[]>;
  /** Show a view: its shape becomes the dashboard, its report the editor's.
   *  Resolves to an error message when it cannot be shown, or null on success —
   *  same contract as its siblings, so a refusal is never silent. */
  selectView: (viewId: number) => string | null;
  /** Persist a patch to the selected view. Resolves to an error message on
   *  failure, or null on success. 409 once the view has been sent (D5). */
  saveView: (patch: UpdateViewBody) => Promise<string | null>;
  /** Persist what the user arranged on the published dashboard INTO the selected
   *  view: its shape and its chart notes. Without this the edits lived in the
   *  session override alone, so every view was a byte-identical copy of the
   *  one publishing minted — versioning that did not view. */
  savePresentation: () => Promise<string | null>;
  /** Open another presentation of the same numbers, optionally duplicating one. */
  /** Open another presentation of the same numbers. The NAME comes from the
   *  person: the backend refuses a blank one (400) instead of deriving one, which
   *  is what removed the duplicate-name race — see SPEC/2102 D2. `copyFrom`
   *  duplicates a view's shape, notes and report. */
  newView: (name: string, copyFrom?: number) => Promise<string | null>;
  /** Report-composition actions (edited before generating). */
  /** Write the note before or after one chart's figure (US 2102). Keyed by chart
   *  id, stable since PR 0 (D15), so a note stays with its figure across runs.
   *  Persisted by savePresentation, like the rest of the presentation. */
  setChartNote: (chartId: string, position: 'before' | 'after', text: string) => void;
  /** Append a fresh block of `kind`, or insert it at index `at` (clamped to the
   *  list) so it can land between charts without being moved step by step
   *  (Task 2766). */
  addBlock: (kind: ReportBlockKind, at?: number) => void;
  updateBlock: <K extends ReportBlockKind>(id: string, kind: K, patch: BlockPatch<K>) => void;
  removeBlock: (id: string) => void;
  moveBlock: (id: string, dir: 'up' | 'down') => void;
  /** "Marcar como enviada": record the client-uploaded PDF (documentKey) and the
   *  recipients on the SELECTED view, and freeze it (D5). The PDF is rendered
   *  and uploaded in the browser (react-pdf), so the caller passes the resulting
   *  storage-hub file id. Nothing is actually mailed until PR 5 — the UI says so. */
  /** Attach the uploaded PDF to the selected view and mark it sent. Resolves
   *  to an error message on failure, or null on success — the caller must NOT
   *  sniff the shared `error` to find out (that races every other action). */
  sendView: (documentKey: string, recipients?: Recipient[]) => Promise<string | null>;
  /** Switch the top-level app section (App.tsx tabs). */
  setSection: (section: TopSection) => void;
  showResults: () => void;
  showConfig: () => void;
  /** Open the published dashboard — the only way into the report (US 2102). */
  showDashboard: () => void;
  /** Open the report of the published reconciliation. Reachable from the
   *  published dashboard only; Gráficos no longer links to it. */
  showReport: () => void;
  /** Select a chart instance in the workspace by its instance id, or clear it. */
  selectInstance: (id: string | null) => void;
  /** Open the per-chart config modal for an instance (also selects it), or close it. */
  /**
   * Open the configuration modal for an instance. `chartId` names WHICH of its
   * figures the Presentation tab should edit — a configured chart can own several
   * (the category split), and without it the tab always edited the first: clicking
   * the gear on the Sulfuro card moved Óxido's palette. Omitted from the surfaces
   * that have no figure in hand (a pending card, the sidebar tree).
   */
  openConfigModal: (id: string, chartId?: string) => void;
  closeConfigModal: () => void;
  /** Ask before deleting `id`; the deletion itself runs when the dialog confirms. */
  requestInstanceDelete: (id: string) => void;
  cancelInstanceDelete: () => void;
  clearError: () => void;
}

export const useStore = create<State>((set, get) => ({
  workspaceId: ENV.workspaceId ?? null,
  workspaceRestored: false,
  reconciliations: [],
  loadingList: false,

  currentId: null,
  name: '',
  description: '',
  draft: emptyDraft(),
  solidNames: {},
  dirty: false,
  saving: false,

  current: null,
  loadingCurrent: false,
  hydratingColumns: false,
  columnsFetchFailed: [],
  running: false,
  runningInstanceIds: null,
  partialRunError: null,
  result: null,
  dashboard: null,
  dashboardOverrides: {},
  loadingViews: false,

  views: [],
  selectedViewId: null,
  savingView: false,
  creatingView: false,
  publishing: false,
  blocks: [],
  chartNotes: {},

  activeTab: 'config',
  // Land on the reconciliations list; opening one (or "Nueva") switches to the
  // "Workspace" section.
  section: 'reconciliaciones',
  configModalInstance: null,
  deleteConfirmInstance: null,
  deleteConfirmReconciliation: null,
  reconciliationDeleteConfirmers: 0,
  configModalChartId: null,
  selectedInstanceId: null,

  error: null,

  init: async (getAccessToken) => {
    // No authzWorkspaceIds yet: there is no workspace to claim until /me answers.
    // Asserting one here is what made the first request of every session name
    // workspace 1 — the old ENV.workspaceId constant.
    configure({
      baseUrl: ENV.conciliatronApi,
      authzUserId: ENV.authzUserId,
    });

    // Which workspace to open is a server-side fact, not a build-time one.
    // ENV.workspaceId is now unset in every deployed environment (config.js writes
    // no workspaceId, and the '1' fallback is gone), so there is normally nothing
    // seeded and the answer comes entirely from core. Local dev seeds it from
    // VITE_WORKSPACE_ID, which is why the seed is still honoured when the caller
    // really is a member of it.
    //
    // /me answers "which workspaces may I act inside", and since the archived
    // filter landed in GetWorkspacesForUser every id it returns is both authorized
    // and active — so the first id is a workspace this app can actually open.
    // WorkspaceSwitcher still reconciles the header's selection against the
    // catalogue on mount, but it no longer has a wrong boot to correct.
    // loadMe shares the response verifySession already got.
    //
    // The workspace the user last picked wins over the seed (Bug 2793). It is read
    // before awaiting /me, and only setWorkspaceId writes it — never from a null
    // workspace, which is how WorkspaceSwitcher's boot-time auto-switch arrives.
    const remembered = get().workspaceRestored ? null : readRememberedWorkspace();
    const me = await loadMe(getAccessToken);
    if (me === null) {
      // The session did not resolve. VerifiedRoute gates on the same call, so
      // this is a race or an outage, not an ordinary state: surface it rather
      // than fetching a workspace we have no reason to believe in.
      set({ error: 'No se pudo confirmar la sesión.' });
      return;
    }
    if (me.workspaceIds.length === 0) {
      // A member of no workspace is a real state. Say so instead of requesting
      // a workspace that is certain to be refused.
      set({ error: 'No tienes acceso a ningún workspace.' });
      return;
    }
    const seeded = get().workspaceId;
    const workspaceId =
      remembered !== null && me.workspaceIds.includes(remembered)
        ? remembered
        : seeded !== null && me.workspaceIds.includes(seeded)
          ? seeded
          : me.workspaceIds[0];
    configure({ authzWorkspaceIds: String(workspaceId) });
    set({ workspaceId, workspaceRestored: true });

    await get().fetchReconciliations();
  },

  fetchReconciliations: async () => {
    const workspaceId = get().workspaceId;
    // Nothing to list before init() resolves one. Silent rather than an error:
    // this is the pre-resolution state, not a failure — init() reports the real
    // problems (no session, no workspaces) itself.
    if (workspaceId === null) return;
    set({ loadingList: true, error: null });
    try {
      const res = await api.listReconciliations(workspaceId);
      // Drop a list that belongs to a workspace we have since left. This was the
      // only async action here without a staleness check, and it is reached twice
      // in a row on every login: init() lists the workspace it resolved, then the
      // header's switcher reconciles that against the catalogue and switches, which
      // lists again. Whichever response landed second won — so the bigger, slower
      // workspace's list could end up on screen under the other one's id, and
      // opening a row from it asked the wrong workspace for that reconciliation and
      // got a 404.
      //
      // Keyed on the workspace rather than pollToken: startNew() bumps that token
      // without starting a list fetch, so a token check would abandon this response
      // and leave loadingList stuck true. Abandoning without clearing the flag is
      // safe on this path because the only writer of workspaceId (setWorkspaceId,
      // and init) always calls this action straight after, and that call owns the
      // flag from there.
      if (get().workspaceId !== workspaceId) return;
      set({ reconciliations: res.items, loadingList: false });
    } catch (e) {
      if (get().workspaceId !== workspaceId) return;
      set({ error: errMessage(e), loadingList: false });
    }
  },

  startNew: () => {
    pollToken++;
    set({
      currentId: null,
      name: '',
      description: '',
      draft: emptyDraft(),
      solidNames: {},
      dirty: false,
      // The banner is shared, so it must go with the reconciliation that raised
      // it: a message about a view that could not be shown is not a fact about
      // the blank reconciliation the user just started. Before this, it survived
      // until the next execute() — the only other place that clears it.
      error: null,
      current: null,
      loadingCurrent: false,
      running: false,
      runningInstanceIds: null,
      partialRunError: null,
      result: null,
      dashboard: null,
      loadingViews: false,
      views: [],
      selectedViewId: null,
      // Reset beside savingView, and for a sharper reason than a stuck
      // Guardar: save() returns early without clearing this when its request
      // is superseded, and Publicar is gated on `saving || dirty` — so leaving
      // it true makes publishing unreachable until a page reload.
      saving: false,
      savingView: false,
  creatingView: false,
      publishing: false,
      blocks: [],
      chartNotes: {},
      activeTab: 'config',
      section: 'espacio-trabajo',
      configModalInstance: null,
      deleteConfirmInstance: null,
      configModalChartId: null,
      selectedInstanceId: null,
    });
  },

  setWorkspaceId: (workspaceId) => {
    const previous = get().workspaceId;
    if (workspaceId === previous) return;
    pollToken++;
    configure({ authzWorkspaceIds: String(workspaceId) });
    // From null this is WorkspaceSwitcher adopting its first entry while init()
    // awaits /me, not a choice — remembering it would overwrite the user's.
    if (previous !== null) rememberWorkspace(workspaceId);
    set({
      workspaceId,
      currentId: null,
      name: '',
      description: '',
      draft: emptyDraft(),
      solidNames: {},
      dirty: false,
      current: null,
      loadingCurrent: false,
      running: false,
      runningInstanceIds: null,
      partialRunError: null,
      // Not merely cosmetic: a save()/saveView() in flight when the switch
      // happens is guarded (see below) to skip writing its stale result into this
      // now-cleared state, which means it also skips clearing its own busy flag —
      // reset both here instead, or the "Guardar" button stays disabled forever.
      saving: false,
      result: null,
      dashboard: null,
      loadingViews: false,
      views: [],
      selectedViewId: null,
      savingView: false,
  creatingView: false,
      publishing: false,
      blocks: [],
      chartNotes: {},
      activeTab: 'config',
      configModalInstance: null,
      deleteConfirmInstance: null,
      configModalChartId: null,
      selectedInstanceId: null,
      reconciliations: [],
    });
    // An unanswered reconciliation-delete ask named a row of the old workspace;
    // confirming it now would aim the DELETE at the new one (Bug 2797).
    get().answerReconciliationDelete(false);
    void get().fetchReconciliations();
  },

  openReconciliation: async (id) => {
    // Claim the token so a rapid A→B→C switch (or an in-flight execute poll)
    // can't let a slow GET write a superseded reconciliation into the store.
    const token = ++pollToken;
    // Pick the tab optimistically from the status already cached in the sidebar
    // list, so switching to an executed reconciliation lands on results without
    // flashing the config editor for the duration of the GET.
    const listed = get().reconciliations.find((r) => r.id === id);
    // Land on results for a reconciliation that has run OR is still in flight
    // (Queued): a resumed run shows its live "Ejecutando…" there (ResultsView),
    // not on the config editor.
    // Where opening a reconciliation lands. A PUBLISHED one opens on its
    // dashboard (D8): its analysis is sealed, so Gráficos would offer nothing to
    // do, and the dashboard is what the report is made of.
    const landingTab = (r: Reconciliation): MainTab =>
      isPublished(r)
        ? 'dashboard'
        : hasBeenExecuted(r.statusId) || isPending(r.statusId)
          ? 'results'
          : 'config';
    const optimisticTab: MainTab = listed ? landingTab(listed) : 'config';
    set({
      error: null,
      running: false,
      runningInstanceIds: null,
      partialRunError: null,
      result: null,
      dashboard: null,
      loadingViews: false,
      current: null,
      loadingCurrent: true,
      views: [],
      selectedViewId: null,
      // Reset beside savingView, and for a sharper reason than a stuck
      // Guardar: save() returns early without clearing this when its request
      // is superseded, and Publicar is gated on `saving || dirty` — so leaving
      // it true makes publishing unreachable until a page reload.
      saving: false,
      savingView: false,
  creatingView: false,
      publishing: false,
      blocks: [],
      chartNotes: {},
      activeTab: optimisticTab,
      section: 'espacio-trabajo',
      configModalInstance: null,
      deleteConfirmInstance: null,
      configModalChartId: null,
      selectedInstanceId: null,
    });
    try {
      const rec = await api.getReconciliation(requireWorkspaceId(get().workspaceId), id);
      if (token !== pollToken) return; // superseded by a newer open/execute
      const derived = deriveResults(rec, get().dashboardOverrides);
      const { result } = derived;
      // Guardar persists the SHAPE (working_dashboard_config); this is the half
      // that reads it back. Without it the column was write-only and every
      // reload silently rebuilt the default presentation — the colours, chart
      // types and order the user arranged were lost, which is precisely what
      // Guardar promises not to do (D10).
      //
      // Re-attaches the live series to the stored shape, the same way
      // selectView does for a view: what is persisted is presentation
      // only, and the data is re-derivable from the result.
      const restored =
        derived.dashboard && hasCharts(rec.workingDashboardConfig)
          ? withDerivedReferenceLines(
              reattachChartData(rec.workingDashboardConfig, derived.dashboard),
              derived.dashboard
            )
          : derived.dashboard;
      const dashboard = withoutDeletedCharts(restored, result, rec);
      // A fresh session override that still names a deleted chart would put it
      // back through resolveDashboard (the next Guardar's shape): it is pruned by
      // the same rule, keeping the session's other edits. A stale one is never read.
      const { [rec.id]: override, ...otherOverrides } = get().dashboardOverrides;
      const freshOverride = override?.version === (rec.executedAt ?? null) ? override : undefined;
      const prunedOverride =
        freshOverride && withoutDeletedCharts(freshOverride.config, result, rec);
      const dashboardOverrides =
        !freshOverride || prunedOverride === freshOverride.config
          ? get().dashboardOverrides
          : prunedOverride
            ? { ...otherOverrides, [rec.id]: { ...freshOverride, config: prunedOverride } }
            : otherOverrides;
      // Re-opening an already-executed reconciliation lands on its results (not
      // the config editor); a failed one lands there too, showing the error.
      set({
        currentId: rec.id,
        current: rec,
        loadingCurrent: false,
        name: rec.name,
        description: rec.description,
        draft: batchRequestToDraft(rec.config),
        dirty: false,
        result,
        dashboard,
        dashboardOverrides,
        // True whenever loadViews is about to run below (same condition as
        // that call): this `dashboard` is only the publish-time snapshot for a
        // published reconciliation, and loadViews' selectView is what applies
        // the actually-selected view's (possibly edited) presentation over it.
        // Consumers must not paint this value as final until that resolves.
        loadingViews: Boolean(dashboard),
        // A run still in flight shows as running so ResultsView renders its
        // progress state (and the poll resumes below). A partial one (Bug 2609)
        // leaves the status alone and keeps the other charts on screen.
        running: isPending(rec.statusId) || isPartialRunPending(rec),
        runningInstanceIds: isPartialRunPending(rec) ? rec.pendingInstanceIds! : null,
        partialRunError: isPartialRunPending(rec) ? null : lastRunErrorMessage(rec),
        activeTab: landingTab(rec),
        error: rec.statusId === ReconciliationStatus.Error ? executionErrorMessage(rec) : null,
      });
      // Repopulate column dropdowns from storage-hub (fire-and-forget; the
      // editor renders immediately and dropdowns fill in when it resolves).
      void get().hydrateReaderColumns();
      // Same for spatial-filter solids: show names instead of raw ids on reopen.
      void get().hydrateSolidNames();
      // Reopened mid-execution: resume polling so the status/result land live
      // without re-dispatching the worker (the run is already enqueued).
      if (isPending(rec.statusId) || isPartialRunPending(rec)) void pollUntilSettled(rec.id, token);
      // Read-back: if the reconciliation has a plottable result, restore the last
      // selected view's presentation over the freshly-derived default.
      if (dashboard) void get().loadViews(rec.id);
    } catch (e) {
      if (token === pollToken) set({ error: errMessage(e), loadingCurrent: false });
    }
  },

  setName: (v) => set({ name: v, dirty: true }),
  setDescription: (v) => set({ description: v, dirty: true }),

  restoreDraft: ({ draft, dirty }) => set({ draft, dirty }),

  addReaderForFile: (file) => {
    const { draft } = get();
    // Mint from the highest existing rN suffix + 1 (not readers.length) so an
    // id is never reused after a removal — a reused id would silently re-bind
    // algorithm models that still point at the old reader.
    const maxSuffix = draft.readers.reduce((max, r) => {
      const match = /^r(\d+)$/.exec(r.id);
      return match ? Math.max(max, Number(match[1])) : max;
    }, 0);
    const id = `r${maxSuffix + 1}`;
    // Auto-fill the grid + columns from storage-hub metadata where present,
    // falling back to READER_DEFAULTS. All fields stay user-editable.
    const fields = readerFieldsFromMetadata(file.metadata);
    const reader: ReaderInput = {
      id,
      fileId: file.id,
      fileName: fileDisplayName(file.filename, file.metadata),
      delimiter: fields.delimiter ?? READER_DEFAULTS.delimiter,
      hasHeader: READER_DEFAULTS.hasHeader,
      coordinatesIndexes: fields.coordinatesIndexes ?? READER_DEFAULTS.coordinatesIndexes,
      delta: fields.delta ?? READER_DEFAULTS.delta,
      min: fields.min ?? READER_DEFAULTS.min,
      max: fields.max ?? READER_DEFAULTS.max,
    };
    if (fields.availableColumns.length > 0) reader.availableColumns = fields.availableColumns;
    if (fields.allColumns.length > 0) reader.allColumns = fields.allColumns;
    // Bind unbound models (readerId '') to the new reader — a chart added
    // before any file is born unbound, and without this the file lands but the
    // chart's column pickers stay empty (US 2095). Mirror of addAlgorithm's
    // readers[0] default, so file-then-chart and chart-then-file end up in the
    // same draft. Models already bound to a reader are never rebound.
    const algorithms = draft.algorithms.map((a) => ({
      ...a,
      models: a.models.map((m) => (m.readerId === '' ? { ...m, readerId: id } : m)),
    }));
    set({ draft: { ...draft, readers: [...draft.readers, reader], algorithms }, dirty: true });
  },

  updateReader: (index, patch) => {
    const { draft } = get();
    const readers = draft.readers.map((r, i) => (i === index ? { ...r, ...patch } : r));
    set({ draft: { ...draft, readers }, dirty: true });
  },

  removeReader: (index) => {
    const { draft } = get();
    const removed = draft.readers[index];
    const readers = draft.readers.filter((_, i) => i !== index);
    // Clear any algorithm model still bound to the removed reader so we never
    // emit a dangling reader_id / columns from a file that's gone. Blanking
    // (rather than remapping) surfaces "— sin archivos —" in the card, forcing
    // a deliberate reselection instead of silently picking a wrong file.
    const algorithms = removed
      ? draft.algorithms.map((a) => ({
          ...a,
          models: a.models.map((m) =>
            m.readerId === removed.id ? { ...m, readerId: '', columns: [], filters: [] } : m
          ),
        }))
      : draft.algorithms;
    set({ draft: { ...draft, readers, algorithms }, dirty: true });
  },

  // Re-fetch each reader's storage-hub metadata to repopulate the UI-only
  // fileName + availableColumns (column dropdowns) when a saved reconciliation
  // is reopened. Does NOT touch the saved grid (coords/delta/min/max) or mark
  // dirty — it's hydration, not an edit. Best-effort: missing metadata is left
  // as-is (manual fallback).
  hydrateReaderColumns: async () => {
    const readers = get().draft.readers;
    if (readers.length === 0) return;
    // Flagged for the whole round trip: until it settles, the column pickers
    // must not fall back to manual entry (which bypasses the type filter).
    set({ hydratingColumns: true });
    try {
      const uniqueIds = [...new Set(readers.map((r) => r.fileId))];
      // allSettled: a single bad file id must not drop every reader's hydration.
      const settled = await Promise.allSettled(uniqueIds.map((fid) => fetchFileMetadata(fid)));
      const byId = new Map(
        uniqueIds.map((fid, i) => {
          const r = settled[i];
          return [fid, r.status === 'fulfilled' ? r.value : undefined] as const;
        })
      );
      set({
        columnsFetchFailed: uniqueIds.filter((_, i) => settled[i].status === 'rejected'),
      });
      const cur = get().draft;
      const merged = cur.readers.map((r) => {
        const fetched = byId.get(r.fileId);
        if (!fetched) return r;
        const next = { ...r };
        const fileName = fileDisplayName(fetched.filename, fetched.metadata);
        if (fileName) next.fileName = fileName;
        const fields = readerFieldsFromMetadata(fetched.metadata);
        if (fields.availableColumns.length > 0) next.availableColumns = fields.availableColumns;
        if (fields.allColumns.length > 0) next.allColumns = fields.allColumns;
        return next;
      });
      set({ draft: { ...cur, readers: merged } });
    } finally {
      set({ hydratingColumns: false });
    }
  },

  noteSolidNames: (entries) => set((s) => ({ solidNames: { ...s.solidNames, ...entries } })),

  // Mirror of hydrateReaderColumns for spatial-filter solids: gather every solid
  // id referenced in the draft, fetch names for the ones not already cached, and
  // merge them in. Best-effort (allSettled: a bad id doesn't drop the rest);
  // fire-and-forget on reopen so the editor renders immediately.
  hydrateSolidNames: async () => {
    const { draft } = get();
    const ids = new Set<string>();
    for (const a of draft.algorithms)
      for (const m of a.models)
        for (const flt of m.filters ?? [])
          if (flt.type === 'spatial') for (const id of flt.solids) ids.add(id);
    const known = get().solidNames;
    const missing = [...ids].filter((id) => !(id in known));
    if (missing.length === 0) return;
    const settled = await Promise.allSettled(missing.map((id) => fetchFileMetadata(id)));
    const entries: Record<string, string> = {};
    missing.forEach((id, i) => {
      const r = settled[i];
      const name =
        r.status === 'fulfilled' && r.value
          ? fileDisplayName(r.value.filename, r.value.metadata)
          : undefined;
      if (name) entries[id] = name;
    });
    if (Object.keys(entries).length > 0) get().noteSolidNames(entries);
  },

  addAlgorithm: (name) => {
    const { draft, current } = get();
    const readerId = draft.readers[0]?.id ?? '';
    // Model 1 (CP) defaults to the SECOND uploaded file when one exists, so a
    // fresh two-model chart never opens comparing a file against itself
    // (#2402). Falls back to the same reader as model 0 when only one file
    // (or none) has been uploaded — nothing else to default to yet.
    const secondReaderId = draft.readers[1]?.id ?? readerId;
    // Snapshot the config of an already-set-up algorithm so a new one doesn't
    // force re-typing every field: prefer the last of the SAME type (its params
    // apply verbatim), else fall back to the last added one. Only parameters and
    // the LP/CP model mapping carry over; the rest starts fresh.
    const reference =
      [...draft.algorithms].reverse().find((a) => a.name === name) ??
      draft.algorithms[draft.algorithms.length - 1];
    // Minted here and carried from now on. `takenInstanceIds` includes the
    // EXECUTED config, not just the draft: a chart deleted a moment ago is still on
    // screen, and reusing its id would hand its figures to this one.
    //
    // CAUGHT, not left to propagate. This runs inside a click handler, and React
    // does not route a handler's throw to an error boundary — nor is there one in
    // this app — so an uncaught one would simply make the card fail to appear: a
    // silent no-op, which is the very outcome mintInstanceId refuses to produce by
    // returning a weak id. Routed to `error`, its message reaches the banner.
    let instanceId: string;
    try {
      instanceId = mintInstanceId(name, takenInstanceIds(draft, current?.config));
    } catch (e) {
      set({ error: errMessage(e) });
      return;
    }
    // Bare algorithm label (SPEC/2240) — a tonnage chart follows its
    // default-checked measures instead, same rule updateAlgorithm applies live.
    const label = isTonnageAlgorithm(name)
      ? tonnageFigureLabel(name, undefined)
      : (ALGORITHM_OVERLAY[name]?.label ?? name);
    const algo: AlgorithmInput = {
      name,
      instanceId,
      figureTitle: mintFigureTitle(label, figureTitlesInUse(draft)),
      models: reference
        ? structuredClone(reference.models)
        : [
            { readerId, columns: [] },
            { readerId: secondReaderId, columns: [] },
          ],
      parameters: inheritedParameters(name, reference?.parameters),
    };
    // `error: null` here and not on the way in: the banner (App.tsx) only comes
    // down with "Cerrar", so a mint that failed once would leave its message
    // standing over the retry that worked. Nothing to clear before the attempt,
    // and one `set` spares a render showing a chart with a stale error above it.
    set({ draft: { ...draft, algorithms: [...draft.algorithms, algo] }, dirty: true, error: null });
  },

  updateAlgorithm: (index, a) => {
    const { draft } = get();
    const prev = draft.algorithms[index];
    // A tonnage chart's default name follows which measures are checked
    // (SPEC/2240) — but only while it still reads exactly like what minting
    // it fresh, right now, against its current siblings would produce. That
    // is the only signal available for "still the auto default, not a name
    // the user typed" (re-minting, not a stored flag, so it also keeps
    // working if a sibling's own rename freed up an unsuffixed label
    // meanwhile). The moment it diverges — any manual edit — this stops
    // touching it.
    const siblingTitles = draft.algorithms
      .filter((_, i) => i !== index)
      .map((x) => x.figureTitle)
      .filter((t): t is string => !!t);
    const next: AlgorithmInput =
      isTonnageAlgorithm(a.name) &&
      a.plotMeasures !== prev?.plotMeasures &&
      prev?.figureTitle ===
        mintFigureTitle(tonnageFigureLabel(prev?.name ?? a.name, prev?.plotMeasures), siblingTitles)
        ? { ...a, figureTitle: mintFigureTitle(tonnageFigureLabel(a.name, a.plotMeasures), siblingTitles) }
        : a;
    const algorithms = draft.algorithms.map((x, i) => (i === index ? next : x));
    // A mode switch can RENAME the algorithm (tonnage by_cutoff ↔ by_categories,
    // via setTonnageMode) and US 2079 had to remap every id the UI was holding,
    // because an id spelled the name. A minted id doesn't (see
    // algorithmInstance.ts): the entry keeps its identity through the rename, so
    // the open config modal and the selection go on resolving untouched.
    set({ draft: { ...draft, algorithms }, dirty: true });
    // A rename (SPEC/2240, including the automatic one above) no longer rewrites
    // the drawn chart's title here: this runs per keystroke, and a new dashboard
    // is a full Plotly redraw of every figure it touches — a scatter of one SVG
    // point per block lagged the "Nombre de la figura" field (bug "Algoritmo:
    // Gráfico de dispersión"). The tree, card label and modal header read the
    // draft and follow live; the figure's own title follows on save().
  },

  removeAlgorithm: (index) => {
    const { draft } = get();
    const algorithms = draft.algorithms.filter((_, i) => i !== index);
    set({ draft: { ...draft, algorithms }, dirty: true });
  },

  // The copy is APPENDED rather than inserted next to its original. That used to
  // be an identity constraint — ids were positional, so inserting renumbered
  // every later entry of the same algorithm — and it no longer is: ids are minted
  // (see algorithmInstance.ts) and survive any insertion. What keeps the append
  // is layout: the dashboard already draws the pending copy beside its original
  // (ResultDashboard's copyOrigin), and inserting would instead reshuffle the
  // grid order of charts the user has already arranged.
  duplicateAlgorithm: (index) => {
    const { draft, current } = get();
    const orig = draft.algorithms[index];
    if (!orig) return;
    // A copy is a NEW chart: it gets its own identity, or it would answer to the
    // original's and the two would be indistinguishable to every consumer. Caught
    // for the same reason as addAlgorithm: a throw here reaches a click handler,
    // where nothing would surface it.
    let instanceId: string;
    try {
      instanceId = mintInstanceId(orig.name, takenInstanceIds(draft, current?.config));
    } catch (e) {
      set({ error: errMessage(e) });
      return;
    }
    const copy: AlgorithmInput = {
      ...structuredClone(orig),
      instanceId: instanceId,
      figureTitle: mintFigureTitle(
        orig.figureTitle ?? ALGORITHM_OVERLAY[orig.name]?.label ?? orig.name,
        figureTitlesInUse(draft)
      ),
    };
    const algorithms = [...draft.algorithms, copy];
    // Clears a failed attempt's banner, see addAlgorithm.
    set({ draft: { ...draft, algorithms }, dirty: true, error: null });
  },

  save: async () => {
    const { currentId, description, draft, workspaceId } = get();
    if (workspaceId === null) return set({ error: NO_WORKSPACE }), null;
    // Name is required (the backend enforces it on create and update). Abort with
    // the shared message rather than inventing a default — the editor surfaces it
    // inline; this guards other callers (e.g. Re-ejecutar).
    const name = get().name.trim();
    const nameError = validateName(name);
    if (nameError) {
      set({ error: nameError });
      return null;
    }
    // Figure names must be set and unique (SPEC/2240) — surfaced inline
    // per-field as the user types, but blocked here at the true boundary, same
    // as the reconciliation's own name above.
    if (draft.algorithms.some((a) => !(a.figureTitle ?? '').trim())) {
      set({ error: 'El nombre de una figura no puede estar vacío.' });
      return null;
    }
    const dupTitle = draft.algorithms.find((a, i) => {
      const at = a.figureTitle?.trim();
      return at && draft.algorithms.some((b, j) => j !== i && b.figureTitle?.trim() === at);
    })?.figureTitle;
    if (dupTitle) {
      set({ error: `Hay más de una figura llamada "${dupTitle}" — el nombre debe ser único.` });
      return null;
    }
    // Publishing seals the ANALYSIS, not the label (D1 + D7). So on a published
    // reconciliation this sends name/description ONLY, and never reaches the
    // config path below.
    //
    // AFTER the name checks above, not before: a rename is still a name, and a
    // blank or duplicate one must not reach the row just because the row is
    // sealed.
    //
    // Why the narrowing rather than a refusal: the backend has always allowed
    // the rename (TestPublished_Update_RenameAllowed) and always refused the
    // WHOLE request when a sealed row's config is touched
    // (TestPublished_Update_RenameWithConfigIsRefusedWhole) — and save() bundles
    // `config` into every PATCH. So a blanket refusal here and the 409 it
    // replaced both closed a door D7 says is open, and closed it on the one
    // thing a user most needs after publishing: fixing a bad name.
    //
    // It also closes a dead end. A single keystroke in Nombre sets `dirty`, and
    // Clonar — the escape the sealed band tells the user to take — is itself
    // gated on `dirty`. With Guardar refused there was no way out but a reload.
    //
    // savePresentation is a different call and is deliberately not touched here:
    // it writes the VIEW, and a sealed analysis is exactly what a view exists to
    // re-present (D5).
    if (currentId != null && isPublished(get().current)) {
      // Claim the token (unlike the save() path below, which only reads —
      // execute() has already claimed one before calling that path, and this
      // one is never reached through execute(): it refuses outright on a
      // published reconciliation before ever claiming a token). Nothing else
      // claims on this branch's behalf, so claiming here detects a workspace
      // switch, startNew, or a navigation racing this PATCH — otherwise a
      // stale response would resurrect another context's data into
      // `current`/`name` or upsert it into the wrong workspace's
      // `reconciliations` list.
      const token = ++pollToken;
      set({ saving: true, error: null });
      try {
        const rec = await api.updateReconciliation(workspaceId, currentId, { name, description });
        if (token !== pollToken) return null;
        set((st) => ({
          current: rec,
          name: rec.name,
          dirty: false,
          saving: false,
          reconciliations: upsertList(st.reconciliations, rec),
        }));
        return rec.id;
      } catch (e) {
        if (token !== pollToken) return null;
        set({ error: errMessage(e), saving: false });
        return null;
      }
    }
    // Read (don't claim) the token: execute() has already claimed one before calling
    // save(), and save() bumping it again here would make execute()'s own post-save
    // check (`token !== pollToken`) always look superseded. Reading it is enough to
    // detect a workspace switch (which does claim) racing this save.
    const token = pollToken;
    set({ saving: true, error: null });
    const config = buildBatchRequest(draft);
    // The config the charts ON SCREEN were derived from — the baseline the edit's
    // delta is subtracted from (see below). The EXECUTED one, like every other
    // correlation site: deriveResults built the shown dashboard from it, so reading
    // the last-saved config instead names ids that are not on screen, and a chart
    // whose analysis the user already replaced is never withdrawn. Read before the
    // request, because `current` is overwritten by its response.
    const previousConfig = executedConfigOf(get().current);
    // …and the one it was DRAWN with, which differs only in the saved
    // presentation-only fields (presentedConfigOf, SPEC/2515).
    const previousPresented = presentedConfigOf(get().current);
    const shownBefore = resolveDashboard(get());
    const resultBefore = get().result;
    const current = get().current;
    // Guardar persists the SHAPE alongside the analysis, so reopening a
    // reconciliation restores the presentation the user arranged — colours, chart
    // types, order — and not just its algorithms. Before this, the shape lived in
    // the session override alone and every reload silently discarded it.
    //
    // Stripped of inline chart data: the series are re-derivable from the result,
    // and inlining them can exceed the API body limit (see snapshotData). Absent
    // on create — a reconciliation has no dashboard before its first run.
    //
    // Persisted ALREADY redrawn for a `plot_measures` change (SPEC/2515): the
    // in-session redraw below only lands after the response, and a shape persisted
    // before it would restore the pre-edit series on the next open.
    // …and with an `error_tolerance` change's lines already moved (SPEC/2423).
    const presentedBefore = resultBefore && resultToDashboard(resultBefore, previousPresented);
    const presentedAfter =
      resultBefore &&
      current &&
      resultToDashboard(resultBefore, presentedConfigOf({ ...current, config }));
    const shape =
      shownBefore && presentedBefore && presentedAfter
        ? applyChartLimits(
            applyChartRedraw(shownBefore, presentedBefore, presentedAfter),
            presentedBefore,
            presentedAfter
          )
        : shownBefore;
    try {
      const rec =
        currentId != null
          ? await api.updateReconciliation(workspaceId, currentId, {
              name,
              description,
              config,
              ...(shape
                ? {
                    // Derived subtitles are re-derived on open rather than stored,
                    // so the next run's parameters (the error cut-off) caption it.
                    workingDashboardConfig: stripChartData(
                      presentedBefore && presentedAfter
                        ? withoutDerivedSubtitles(shape, [presentedBefore, presentedAfter])
                        : shape
                    ),
                  }
                : {}),
            })
          : await api.createReconciliation(workspaceId, { name, description, config });
      // Superseded by a workspace switch mid-save — rec belongs to the workspace we
      // navigated away from; setWorkspaceId already cleared/reset this state, don't
      // resurrect it. `saving` was already reset too (see setWorkspaceId).
      if (token !== pollToken) return null;
      // Reflect the server snapshot and keep the sidebar fresh via a local upsert
      // (no full-list refetch on every save/execute).
      set((s) => ({
        currentId: rec.id,
        current: rec,
        name: rec.name,
        dirty: false,
        saving: false,
        reconciliations: upsertList(s.reconciliations, rec),
      }));
      // A presentation-only edit (`error_bases`, `plot_measures`) changes WHICH
      // charts the stored result draws without changing a number, so nothing is
      // re-run — and nothing re-derived the dashboard either: `deriveResults` only
      // runs on open/execute, and the session override still matches `executedAt`,
      // so it kept winning. The selector looked inert until the reconciliation was
      // reopened. Apply just the difference the edit makes, so the user's own
      // dashboard edits (and their deletions) survive it.
      const result = get().result;
      const shown = get().dashboard;
      if (result && shown && previousConfig) {
        // Only add what the saved config OWNS. A structural edit (deleting an
        // analysis) leaves the stored result with more envelopes than the config has
        // entries, and the leftovers draw charts under a guessed identity — an
        // artifact of that mismatch, not something the edit created. Adding them is
        // what made a deleted figure reappear, unconfigured, on Guardar.
        const owners = chartInstanceMap(result, rec.config);
        const owned = new Set(executedInstanceIds(rec.config));
        const regrouped = applyChartSetChange(
          shown,
          resultToDashboard(result, previousConfig),
          resultToDashboard(result, rec.config),
          (chartId) => owned.has(owners[chartId])
        );
        // `plot_measures` keeps every id and swaps a chart's series, so the id diff
        // above misses it: redraw those charts in place (SPEC/2515).
        // …and write the saved figure names into the charts that draw them, last,
        // so a redrawn chart carries its new name too (SPEC/2240).
        // `error_tolerance` keeps ids and series and moves only a chart's derived
        // reference lines: patch just those (SPEC/2423).
        const presentedBefore = resultToDashboard(result, previousPresented);
        const presentedAfter = resultToDashboard(result, presentedConfigOf(rec));
        const merged = withFigureTitles(
          applyChartLimits(
            applyChartRedraw(regrouped, presentedBefore, presentedAfter),
            presentedBefore,
            presentedAfter
          ),
          owners,
          rec.config
        );
        if (merged !== shown) get().setDashboardOverride(rec.id, merged);
      }
      return rec.id;
    } catch (e) {
      if (token !== pollToken) return null;
      set({ error: errMessage(e), saving: false });
      return null;
    }
  },

  // Persist the latest config, dispatch the worker, then poll the reconciliation
  // until it reaches a terminal status. On success build the dashboard from the
  // result; on failure surface the error. No algorithm is re-run client-side.
  execute: async (instanceIds) => {
    // The seal's own refusal, and it has to live HERE rather than be inherited
    // from save(): save() now succeeds on a published row (it narrows the patch
    // to the label, D7), so leaning on it would let a re-run through. And a
    // re-run is the more damaging half — it overwrites execution_result, which
    // is what EVERY view of a published reconciliation resolves against,
    // including one whose PDF is already with a client (D1 + D3).
    //
    // Spinners cleared alongside the error: a caller may have set them before
    // awaiting, and a run that never started must not leave Publicar disabled.
    if (isPublished(get().current)) {
      set({ error: SEALED_ANALYSIS_MESSAGE, running: false, saving: false });
      return;
    }
    // Claim the cancellation token BEFORE the async save() so a navigation
    // (startNew/openReconciliation, which bump the token) during save abandons
    // this run instead of yanking the user back into a stale results view.
    const token = ++pollToken;
    const id = await get().save();
    // save() failed (it already set the error): clear any spinner left running by
    // a prior run this token bump just abandoned, then bail. BOTH spinners — the
    // token bump above orphaned any in-flight save, and save()'s own validations
    // return before it claims `saving`, so on an invalid draft nobody else clears
    // it. Left true it disables Publicar (gated on `saving || dirty`) until a
    // reload, not just Guardar.
    if (id == null) {
      set({ running: false, saving: false });
      return;
    }
    if (token !== pollToken) return; // superseded during save — that run owns `running`
    // A partial run (Bug 2609) keeps the result on screen: every chart it does
    // not compute is still the truth, and mergePartialDashboard folds the new
    // figures in on landing. A full run clears it, as before.
    const partial = instanceIds?.length ? instanceIds : null;
    set({
      error: null,
      partialRunError: null,
      running: true,
      runningInstanceIds: partial,
      activeTab: 'results',
      ...(partial ? {} : { result: null, dashboard: null }),
    });
    try {
      const snap = await api.executeReconciliation(
        requireWorkspaceId(get().workspaceId),
        id,
        partial ?? undefined
      );
      if (token !== pollToken) return; // superseded
      // Naming every chart is a full run, and the backend dispatches it as one:
      // no plan comes back, the stored result is being replaced — say so.
      if (partial && !isPartialRunPending(snap)) {
        set({ current: snap, runningInstanceIds: null, result: null, dashboard: null });
      } else {
        set({ current: snap });
      }
      // Observe the durable background job until it settles. Transient poll
      // failures are retried inside pollUntilSettled — only the dispatch above
      // (or a 404) reaches this catch, surfacing an error the user can retry.
      await pollUntilSettled(id, token);
    } catch (e) {
      if (token !== pollToken) return;
      if (partial) {
        // Nothing was dispatched and the result on screen is untouched, so this
        // is not the `error` that replaces it. The one refusal a person can act
        // on — another run in flight — is said in the interface's voice.
        set({
          running: false,
          runningInstanceIds: null,
          partialRunError:
            e instanceof ApiError && e.status === 409
              ? 'Hay otra ejecución en curso. Espera a que termine o re-ejecuta la reconciliación completa.'
              : errMessage(e),
        });
      } else {
        set({ error: errMessage(e), running: false });
      }
    }
  },

  // Clone a reconciliation into a brand-new one carrying the exact same
  // configuration. Everything that defines the setup lives in the opaque `config`
  // blob (readers, algorithms, filters, solids, criterio_seleccion), so a faithful
  // clone is just a create of {name, description, config} from the source —
  // runtime/output state (status, results, publication, timestamps) is left to
  // default. We copy the *persisted* config (a fresh GET), then open the copy so
  // the user lands on it (config tab; a clone has never run).
  cloneReconciliation: async (id) => {
    // Read (don't claim, see save()'s comment) — this detects a workspace switch
    // racing the clone without disturbing any token an in-progress caller relies on.
    const token = pollToken;
    set({ error: null });
    try {
      const wsId = requireWorkspaceId(get().workspaceId);
      const src = await api.getReconciliation(wsId, id);
      const created = await api.createReconciliation(wsId, {
        name: copyName(src.name),
        description: src.description,
        config: src.config,
      });
      // Superseded by a workspace switch mid-clone — `created` belongs to wsId, the
      // workspace we navigated away from, not whatever's now active. Bail before
      // both surfacing it in the (now different) reconciliations list and before
      // openReconciliation forces `section` back to "espacio-trabajo" and issues a
      // GET for this id against the new workspaceId.
      if (token !== pollToken) return;
      // Show the new row immediately via a local upsert (same as save/execute —
      // no full-list refetch), then open the copy.
      set((s) => ({ reconciliations: upsertList(s.reconciliations, created) }));
      await get().openReconciliation(created.id);
    } catch (e) {
      if (token !== pollToken) return;
      set({ error: errMessage(e) });
    }
  },

  deleteReconciliation: async (id) => {
    if (get().reconciliationDeleteConfirmers > 0) {
      // One dialog, one answer: a newer ask supersedes an unanswered one.
      pendingDeleteAnswer?.(false);
      const confirmed = await new Promise<boolean>((resolve) => {
        pendingDeleteAnswer = resolve;
        set({ deleteConfirmReconciliation: id });
      });
      if (!confirmed) return;
    }
    try {
      await api.deleteReconciliation(requireWorkspaceId(get().workspaceId), id);
      // If we removed the open reconciliation, abandon any in-flight poll and
      // reset to a clean editor.
      if (get().currentId === id) get().startNew();
      // Drop it from the list locally (no full refetch).
      set((s) => ({ reconciliations: s.reconciliations.filter((r) => r.id !== id) }));
    } catch (e) {
      set({ error: errMessage(e) });
    }
  },

  answerReconciliationDelete: (confirmed) => {
    const answer = pendingDeleteAnswer;
    pendingDeleteAnswer = null;
    set({ deleteConfirmReconciliation: null });
    answer?.(confirmed);
  },

  mountReconciliationDeleteConfirm: () => {
    set((s) => ({ reconciliationDeleteConfirmers: s.reconciliationDeleteConfirmers + 1 }));
    return () => {
      set((s) => ({ reconciliationDeleteConfirmers: s.reconciliationDeleteConfirmers - 1 }));
      // Nobody left to answer: fail closed rather than leave the ask hanging.
      if (get().reconciliationDeleteConfirmers === 0) get().answerReconciliationDelete(false);
    };
  },

  // Record the user's presentation edit for the session, tagged with the current
  // result version (executedAt) so a later re-execution invalidates it. We do NOT
  // feed it back into `dashboard` (dashboard-kit owns the live edit in its own
  // store; re-feeding would loop). The override re-seeds on re-open only when it
  // still matches the result version (see deriveResults).
  setDashboardOverride: (reconciliationId, config, opts) =>
    set((s) => ({
      dashboardOverrides: {
        ...s.dashboardOverrides,
        [reconciliationId]: { version: s.current?.executedAt ?? null, config },
      },
      // Keep the live `dashboard` in sync with the edit so every consumer that
      // reads it — the results view, the report/document, publish — reflects
      // the change immediately. Mirrors deriveResults, which already resolves
      // `dashboard` to the fresh override on open/execute; without this, switching
      // to the document (which reads `s.dashboard`) shows the pre-edit view.
      dashboard: reconciliationId === s.currentId ? config : s.dashboard,
      // Any change the USER made re-enables Guardar. Removing a figure or editing a
      // palette left it disabled, so the one action available after an edit was the
      // one that looked unnecessary — and a delete whose only trace is the override
      // was indistinguishable from nothing having happened.
      //
      // Asserted by the caller rather than implied by the call, because two of the
      // callers are not edits at all: dashboard-kit notifies its host on MOUNT with
      // the very config it was handed, so merely opening a reconciliation marked it
      // unsaved; and save() re-records the override to apply the difference the
      // saved edit makes, which marked it unsaved again the instant it was saved —
      // "Cambios sin guardar", right after Guardar succeeded.
      dirty: opts?.userEdit ? true : s.dirty,
    })),

  // "Publicar": seal the reconciliation and open view #1, in one backend
  // transaction (D1). From here neither the config nor the result can change —
  // the only way to alter the substance is to clone.
  //
  // The shape the user is looking at travels with it: the backend copies
  // working_dashboard_config into view #1, so it must be persisted BEFORE the
  // seal, or publishing silently discards every colour and chart type they chose.
  publish: async (viewName) => {
    const { currentId, workspaceId } = get();
    if (currentId == null) return 'No hay una reconciliación abierta.';
    if (workspaceId === null) return NO_WORKSPACE;
    const token = pollToken;
    set({ publishing: true });
    try {
      const shape = resolveDashboard(get());
      if (shape) {
        await api.updateReconciliation(workspaceId, currentId, {
          workingDashboardConfig: stripChartData(shape),
        });
      }
      // Only sent when the user actually typed one: an empty string would travel
      // as a name and the backend's fallback would never run.
      const named = viewName?.trim();
      const rec = await api.publishReconciliation(
        workspaceId,
        currentId,
        named ? { viewName: named } : undefined
      );
      // Superseded on the SUCCESS path: null is factually right (the seal
      // happened), and the navigation already reset `publishing`. The caller
      // checks the subject before acting on it — see handlePublish.
      if (token !== pollToken) return null;
      set((s) => ({
        publishing: false,
        current: rec,
        dirty: false,
        reconciliations: upsertList(s.reconciliations, rec),
        // Mirrors openReconciliation's own gate (#2449): the caller currently
        // always awaits this whole action before showing the dashboard, so
        // nothing observes this mid-flight today — but a future caller that
        // doesn't wait must not be able to paint a not-yet-view-resolved
        // dashboard either.
        loadingViews: true,
      }));
      await get().loadViews(rec.id);
      return null;
    } catch (e) {
      // NOT `return null` when superseded: null is this action's SUCCESS value, so
      // swallowing a failure here made the caller close the confirmation and
      // navigate as though the irreversible action had gone through. Being
      // superseded says nothing about whether the publish worked — it didn't.
      // The set() is skipped instead, because the navigation already reset
      // `publishing` and must not have its state written over.
      if (token === pollToken) set({ publishing: false });
      // Returned, not routed through the shared `error`: that banner renders as
      // "la ejecución no se completó" and would hide the (valid) dashboard. The
      // caller shows this next to the button the user pressed.
      return errMessage(e);
    }
  },

  // Read-only fetch for the history detail panel. Does not touch the workspace.
  fetchViews: async (reconciliationId) => {
    try {
      const res = await api.listViews(requireWorkspaceId(get().workspaceId), reconciliationId);
      return res.items;
    } catch (e) {
      // Don't hijack the shared execution banner, but DON'T disguise a failure
      // as "no views" either — log it and rethrow so the panel can show a
      // distinct "couldn't load" state.
      console.warn(`fetchViews failed for reconciliation ${reconciliationId}`, e);
      throw e;
    }
  },

  // Read-back on open: load the views and show one. Fired fire-and-forget
  // from openReconciliation, so it carries the navigation token and bails if a
  // newer open/startNew/execute supersedes it mid-flight — a slow resolution
  // must not write another reconciliation's views into the store.
  loadViews: async (reconciliationId) => {
    const { workspaceId } = get();
    if (workspaceId === null) return;
    const token = pollToken;
    try {
      const res = await api.listViews(workspaceId, reconciliationId);
      if (token !== pollToken || get().currentId !== reconciliationId) return;
      set({ views: res.items });
      // Keep the selection if it survived the reload; otherwise show the LAST
      // one, which is the newest. Never items[0] blindly: picking the wrong row
      // and then asking it for a PDF is precisely US 2100.
      const keep = res.items.find((v) => v.id === get().selectedViewId);
      const target = keep ?? res.items[res.items.length - 1] ?? null;
      // Deliberately NOT reported: on load a refusal means the dashboard is not
      // derived yet, which is a window every open passes through, and painting an
      // error banner for it would accuse normal loading. The case where it does not
      // resolve is already explained by the surface, which says the reconciliation
      // has no charts to show. A refusal after a USER ACTION is different and does
      // get reported — see newView.
      if (target) get().selectView(target.id);
      // Cleared LAST, deliberately its own `set` AFTER selectView (which, when
      // a target was found, already swapped `dashboard` to the selected
      // view's presentation): a raw subscriber sees these as separate
      // notifications, so ordering — not batching — is what guarantees no
      // frame ever has this flag false while `dashboard` still carries the
      // pre-edit (publish-time) snapshot. An earlier version cleared this
      // alongside `views` instead, BEFORE selectView, and a subscribe-level
      // test caught the resulting bad frame (#2449).
      set({ loadingViews: false });
    } catch (e) {
      if (token !== pollToken) return;
      set({ error: errMessage(e), loadingViews: false });
    }
  },

  // Show a view: its shape becomes the dashboard, its report the editor's.
  //
  // The stored shape carries presentation only (stripChartData), so the data is
  // re-attached from the freshly derived dashboard. That is safe here in a way it
  // never was before: publishing sealed the result, so what it re-attaches to
  // cannot have changed underneath it (D3).
  selectView: (viewId) => {
    const s = get();
    const v = s.views.find((x) => x.id === viewId);
    if (!v) return 'Esa vista ya no existe.';
    const live = s.dashboard;
    // All or nothing. Applying the title, blocks and notes of view B while the
    // charts stayed on A marked the chip selected and showed the wrong figures,
    // silently — and savePresentation would then write A's shape into B.
    if (!live) return 'No se puede mostrar esta vista: los gráficos aún no están disponibles.';
    // A view is sealed, and one published before SPEC/2515 may be stored under the
    // EXECUTED measures while `live` derives the saved ones: the executed
    // derivation is the fallback that keeps it showing what was sent.
    const executedLive = s.result
      ? resultToDashboard(s.result, executedConfigOf(s.current))
      : undefined;
    const restored = reattachChartData(v.dashboardConfig, live, executedLive);
    set((prev) => ({
      selectedViewId: v.id,
      // No docTitle: the title travels INSIDE the blocks (main's title block), so
      // rehydrating them rehydrates it.
      blocks: v.reportBlocks ?? [],
      chartNotes: v.chartNotes ?? {},
      dashboard: restored,
      // The override must move with the selection: resolveDashboard reads it, so
      // leaving A's behind made savePresentation write A's shape into B.
      dashboardOverrides: {
        ...prev.dashboardOverrides,
        [v.reconciliationId]: {
          version: prev.current?.executedAt ?? null,
          config: restored,
        },
      },
    }));
    return null;
  },

  // Persist a patch to the selected view.
  saveView: async (patch) => {
    const { currentId, selectedViewId, workspaceId } = get();
    if (currentId == null || selectedViewId == null) {
      return 'No hay una vista seleccionada.';
    }
    if (workspaceId === null) return NO_WORKSPACE;
    const token = pollToken;
    set({ savingView: true });
    try {
      const updated = await api.updateView(workspaceId, currentId, selectedViewId, patch);
      if (token !== pollToken) return null;
      set((s) => ({ savingView: false, views: upsertView(s.views, updated) }));
      return null;
    } catch (e) {
      if (token !== pollToken) return null;
      set({ savingView: false });
      // saveView carries the rename too, so it can land on the same index.
      return viewNameErrMessage(e);
    }
  },

  // The shape the user is looking at, stripped of inline series (they are
  // re-derivable from the sealed result, and inlining them can exceed the API
  // body limit — see snapshotData). Same rule as save() and publish(), read
  // through resolveDashboard so a fresh session override wins over the derived
  // dashboard, exactly as every other consumer resolves it.
  savePresentation: async () => {
    const shape = resolveDashboard(get());
    if (!shape) return 'No hay una presentación para guardar.';
    return get().saveView({
      dashboardConfig: stripChartData(shape),
      chartNotes: get().chartNotes,
      // The composed report travels with the presentation. selectView already
      // rehydrates `blocks` from the view (D9), so writing only the shape left the
      // read half with nothing to read: every reload discarded captions, blocks
      // and title, and a SENT view stored a document that did not match the PDF
      // the client received.
      reportBlocks: get().blocks,
      // DERIVED from the title block, not stored twice: the column is a mirror so
      // a list can show the report's title without parsing its blocks.
      title: reportTitle(get().blocks),
    });
  },

  // Another presentation of the same sealed numbers (D2). Duplicating is what
  // makes a sent view's freeze bearable: you copy it and keep working.
  newView: async (name, copyFrom) => {
    const { currentId, workspaceId } = get();
    if (currentId == null) return 'No hay una reconciliación abierta.';
    if (workspaceId === null) return NO_WORKSPACE;
    // Checked here as well as by the backend: the dialog can then say it without a
    // round trip, and the store never sends a request it knows will be refused.
    // Through the shared rule, not a copy of its message — the literal used to live
    // here too, so the two could drift, and this guard only checked for blank while
    // the rule also caps the length.
    const nameError = validateViewName(name);
    if (nameError) return nameError;
    const trimmed = name.trim();
    const token = pollToken;
    // Its OWN flag: sharing savingView made the save button read "Guardando…"
    // while a view was being CREATED — a false statement about a different
    // action — and let both run at once.
    set({ creatingView: true });
    try {
      const created = await api.createView(workspaceId, currentId, { name: trimmed, copyFrom });
      // THE ROW EXISTS FROM HERE ON, so nothing below may return a message: the
      // caller reads one as "it was not created" and offers a retry — and the retry
      // now hits the unique index (migration 0006), so the user is told the name is
      // taken by a view they were just told does not exist. There is no way out of
      // that: neither the name they wanted nor the knowledge that they already have
      // it. Before the index the retry quietly produced "Vista N+1"; the guarantee
      // turned a loose message into a trap.
      set({ creatingView: false });
      // Superseded: the subject is gone, so this reports nothing at all. Accusing
      // the reconciliation the user moved TO about one they moved away from is worse
      // than silence, and whatever bumped the token already reset this surface.
      if (token !== pollToken) return null;
      set((s) => ({ views: upsertView(s.views, created) }));
      // Selecting is what puts the copy on screen, and it can legitimately refuse
      // (no dashboard derived yet). That is a display problem about a view that
      // EXISTS, so it goes to the surface, not to the caller.
      const showErr = get().selectView(created.id);
      if (showErr) set({ error: showErr });
      return null;
    } catch (e) {
      // Nothing was created on this path, so a message here is the truth.
      set({ creatingView: false });
      if (token !== pollToken) return null;
      return viewNameErrMessage(e);
    }
  },


  setChartNote: (chartId, position, text) =>
    set((s) => {
      const note: ChartNote = { ...s.chartNotes[chartId], [position]: text };
      const next = { ...s.chartNotes };
      // An emptied note is REMOVED rather than stored as ''. chart_notes is the
      // view's payload: a map of empty strings is noise, and it would also make
      // two views differ for no reason a reader could see. Trimmed only for the
      // decision — the text is stored as typed.
      if (!note.before?.trim() && !note.after?.trim()) delete next[chartId];
      else next[chartId] = note;
      return { chartNotes: next };
    }),
  // Minting a block id needs a randomness source and refuses to invent a weak one
  // (see randomSuffix): a colliding block id is the wrong figure in a client's PDF,
  // not a degradation. CAUGHT for the same reason addAlgorithm catches — this runs
  // in a click handler, and React routes a handler's throw to no error boundary,
  // so leaving it would make "Agregar bloque" a silent no-op.
  addBlock: (kind, at) => {
    try {
      set((s) => ({ blocks: insertBlock(s.blocks, newBlock(kind), at) }));
    } catch (e) {
      set({ error: errMessage(e) });
    }
  },
  // Patch only the block matching both id and kind; the kind-keyed BlockPatch<K>
  // guarantees `patch` carries only fields valid for that variant, so the merge
  // stays a well-formed ReportBlock with no cast.
  updateBlock: (id, kind, patch) =>
    set((s) => ({
      blocks: s.blocks.map((b) => (b.id === id && b.kind === kind ? { ...b, ...patch } : b)),
    })),
  removeBlock: (id) => set((s) => ({ blocks: s.blocks.filter((b) => b.id !== id) })),
  moveBlock: (id, dir) => set((s) => ({ blocks: reorderBlocks(s.blocks, id, dir) })),

  // "Marcar como enviada" (D13). The PDF was rendered and uploaded to storage-hub
  // in the browser; the caller passes its file id. We record it and the
  // recipients on the SELECTED view and freeze it (D5).
  //
  // Nothing is mailed: the repo has no sender until PR 5, so this is a person
  // asserting the report went out. The UI says exactly that — an app that claims
  // to have delivered something it never touched is worse than no feature.
  sendView: async (documentKey, recipients) => {
    const { currentId, selectedViewId, workspaceId } = get();
    // Read (don't claim) the token, like save(): a navigation mid-send abandons
    // these writes rather than pushing this view and this parent into whatever
    // reconciliation is open by then. PublishModal, unlike ConfirmPublishDialog,
    // can be dismissed in flight, so the window is reachable.
    const token = pollToken;
    // Loud precondition: the caller has ALREADY uploaded the PDF, so a silent
    // no-op here would orphan that file and read as success.
    if (currentId == null || selectedViewId == null) {
      return 'No hay una vista seleccionada para enviar.';
    }
    if (workspaceId === null) return NO_WORKSPACE;
    set({ publishing: true, error: null });
    let sent: ReconciliationView;
    try {
      sent = await api.sendView(workspaceId, currentId, selectedViewId, {
        documentKey,
        distributionList: recipients,
      });
    } catch (e) {
      set({ publishing: false });
      return errMessage(e);
    }
    // Superseded: the send DID happen — sent_at is stamped and the view is
    // frozen — but it belongs to a reconciliation the user has left, and the
    // navigation already cleared this state. Report success (it succeeded) and
    // write nothing; the next open reads the truth from the server.
    if (token !== pollToken) return null;
    // COMMIT THE SEND FIRST. The backend has stamped sent_at and FROZEN the
    // view by now, so anything that fails after this point must not read as
    // "no se pudo enviar": the user would retry, render and upload a SECOND PDF
    // (orphaning it), and get a 409 on a view that did go out.
    set((s) => ({ publishing: false, views: upsertView(s.views, sent) }));
    // The reconciliation's own sentAt badge changes on the first send, so re-read
    // the parent rather than guessing — best effort, and never fatal to the send.
    try {
      const rec = await api.getReconciliation(workspaceId, currentId);
      if (token !== pollToken) return null;
      set((s) => ({ current: rec, reconciliations: upsertList(s.reconciliations, rec) }));
    } catch {
      // The send stands; only the badge is stale until the next list refresh.
    }
    return null;
  },

  setSection: (section) => set({ section }),
  showResults: () => set({ activeTab: 'results' }),
  showConfig: () => set({ activeTab: 'config' }),
  showDashboard: () => set({ activeTab: 'dashboard' }),
  // Entering the report editor: seed the default blocks (intro → one chart block
  // per dashboard chart → conclusions) the first time, so it opens looking like
  // the pre-block-model report. Re-entering keeps the user's edits.
  showReport: () => {
    // Same catch as addBlock, and reachable for the same reason: seeding the
    // default list mints an id per figure. The throw happens inside the updater,
    // before it returns, so a refusal leaves the tab where it was rather than
    // opening an empty report.
    try {
      set((s) => {
        const figures = figuresOf(s.dashboard?.charts ?? [], s.result, s.current);
        return {
          activeTab: 'report' as const,
          // Seeded from the figures on the dashboard, each already tagged with the
          // configured chart that drew it — so the blocks reference identity from
          // the start and a later re-execution cannot repoint them. Every figure is
          // bracketed by its two note blocks (D9).
          //
          // An existing document is NOT re-seeded, but one composed before note
          // blocks existed is migrated onto them once (migrateNoteBlocks returns
          // the same array when there is nothing to do).
          blocks: s.blocks.length
            ? migrateNoteBlocks(s.blocks, figures, s.chartNotes)
            : defaultBlocks(figures),
        };
      });
    } catch (e) {
      set({ error: errMessage(e) });
    }
  },

  selectInstance: (id) => set({ selectedInstanceId: id }),
  openConfigModal: (id, chartId) =>
    set({
      configModalInstance: id,
      configModalChartId: chartId ?? null,
      selectedInstanceId: id,
    }),
  closeConfigModal: () => set({ configModalInstance: null, configModalChartId: null }),
  requestInstanceDelete: (id) => set({ deleteConfirmInstance: id }),
  cancelInstanceDelete: () => set({ deleteConfirmInstance: null }),

  clearError: () => set({ error: null }),
}));
